import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Copy, KeyRound, Plus, ShieldOff } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtDateFull, timeAgo } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tag } from "@/components/common/StatusChip";

const EXPIRY = [["never", "Never expires"], ["30", "30 days"], ["90", "90 days"], ["365", "1 year"]];

function copy(text, what) { navigator.clipboard?.writeText(text); toast.success(`${what} copied`); }

function NewKeyReveal({ created, bot, onDone }) {
  const base = process.env.REACT_APP_BACKEND_URL;
  const curl = `curl -H "Authorization: Bearer ${created.key}" ${base}/api/auth/me`;
  return (
    <div className="space-y-2 border border-emerald-500/50 bg-emerald-500/5 p-3" data-testid="new-key-reveal">
      <div className="text-sm font-semibold text-emerald-600 dark:text-emerald-300">Key “{created.name}” for {bot.name} — copy it now, it will not be shown again.</div>
      <div className="flex items-center gap-2">
        <code className="mono flex-1 break-all border border-border bg-background px-2 py-1.5 text-xs" data-testid="new-key-value">{created.key}</code>
        <Button size="sm" variant="outline" className="h-8" onClick={() => copy(created.key, "Key")} data-testid="new-key-copy-btn"><Copy className="h-3.5 w-3.5" /></Button>
      </div>
      <div className="text-[11px] text-muted-foreground">Send it as <span className="mono">Authorization: Bearer &lt;key&gt;</span> on any <span className="mono">/api/…</span> call — no login step. Test:</div>
      <div className="flex items-center gap-2">
        <code className="mono flex-1 break-all border border-border bg-background px-2 py-1.5 text-[11px] text-muted-foreground">{curl}</code>
        <Button size="sm" variant="outline" className="h-8" onClick={() => copy(curl, "Command")} data-testid="new-key-copy-curl-btn"><Copy className="h-3.5 w-3.5" /></Button>
      </div>
      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onDone} data-testid="new-key-done-btn">I've saved it</Button>
    </div>
  );
}

export default function BotKeysDialog({ bot, onOpenChange }) {
  const qc = useQueryClient();
  const open = !!bot;
  const { data: keys = [] } = useQuery({ queryKey: ["api-keys", bot?.id], queryFn: () => api.get(`/users/${bot.id}/api-keys`).then((r) => r.data), enabled: open });
  const [name, setName] = useState("");
  const [expiry, setExpiry] = useState("never");
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["api-keys", bot?.id] }); qc.invalidateQueries({ queryKey: ["users"] }); };
  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/users/${bot.id}/api-keys`, { name: name.trim() || "default", expires_in_days: expiry === "never" ? null : Number(expiry) });
      setCreated(data); setName(""); refresh();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const revoke = async (k) => {
    try { await api.delete(`/users/${bot.id}/api-keys/${k.id}`); toast.success(`Key “${k.name}” revoked — requests with it now fail`); refresh(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  if (!bot) return null;
  const active = keys.filter((k) => !k.revoked_at);
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setCreated(null); onOpenChange(v); }}>
      <DialogContent className="max-w-2xl" data-testid="bot-keys-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="h-4 w-4 text-primary" /> API keys — {bot.name}</DialogTitle>
          <DialogDescription>Each key signs the bot in instantly with exactly the capabilities ticked for it in the permission matrix. Keys are stored hashed; revoke one the moment it might have leaked.</DialogDescription>
        </DialogHeader>
        {created ? <NewKeyReveal created={created} bot={bot} onDone={() => setCreated(null)} /> : (
          <form onSubmit={create} className="flex flex-wrap items-end gap-2" data-testid="new-key-form">
            <label className="flex-1 text-xs"><span className="eyebrow">Key name</span><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. production, staging" className="mt-1 h-8 text-xs" data-testid="new-key-name-input" /></label>
            <label className="text-xs"><span className="eyebrow">Expires</span>
              <Select value={expiry} onValueChange={setExpiry}><SelectTrigger className="mt-1 h-8 w-36 text-xs" data-testid="new-key-expiry"><SelectValue /></SelectTrigger>
                <SelectContent>{EXPIRY.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></label>
            <Button type="submit" size="sm" className="h-8" disabled={busy || active.length >= 10} data-testid="new-key-create-btn"><Plus className="mr-1 h-3.5 w-3.5" /> {busy ? "Creating…" : "Create key"}</Button>
          </form>
        )}
        <div className="border border-border">
          {keys.length === 0 && <div className="p-4 text-xs text-muted-foreground" data-testid="keys-empty">No keys yet — create one above.</div>}
          {keys.length > 0 && (
            <table className="w-full text-xs" data-testid="keys-table">
              <thead><tr className="border-b border-border bg-muted/40 text-left">{["Name", "Key id", "Created", "Last used", "Expires", "Status", ""].map((h) => <th key={h} className="eyebrow px-2 py-1.5">{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-border">
                {keys.map((k) => (
                  <tr key={k.id} className={k.revoked_at ? "opacity-50" : ""} data-testid={`key-row-${k.key_id}`}>
                    <td className="px-2 py-1.5 font-semibold">{k.name}</td>
                    <td className="mono px-2 py-1.5">tt_{k.key_id}_…</td>
                    <td className="px-2 py-1.5 text-muted-foreground" title={fmtDateFull(k.created_at)}>{timeAgo(k.created_at)}<div className="text-[10px]">by {k.created_by_name}</div></td>
                    <td className="px-2 py-1.5 text-muted-foreground" data-testid={`key-last-used-${k.key_id}`}>{k.last_used_at ? timeAgo(k.last_used_at) : "never"}</td>
                    <td className="px-2 py-1.5 text-muted-foreground">{k.expires_at ? fmtDateFull(k.expires_at) : "never"}</td>
                    <td className="px-2 py-1.5">{k.revoked_at ? <Tag tone="red">revoked</Tag> : k.expires_at && new Date(k.expires_at) < new Date() ? <Tag tone="amber">expired</Tag> : <Tag tone="emerald">active</Tag>}</td>
                    <td className="px-2 py-1.5 text-right">{!k.revoked_at && <button className="inline-flex items-center gap-1 text-red-500 hover:underline" onClick={() => revoke(k)} data-testid={`key-revoke-${k.key_id}`}><ShieldOff className="h-3 w-3" /> revoke</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
